import { Card } from "@caprikorn/dynamo-ui";
import { type FC, useEffect } from "react";
import { useParams } from "react-router-dom";

import { useReferences } from "@contexts/References/References.context";
import { useSelectedPerimeters } from "@contexts/SelectedPerimeters/SelectedPerimeters.context";
import { PerimeterSelfReading } from "@pages/PerimeterSite/PerimeterSelfReading/PerimeterSelfReading";
import { PerimeterSiteMaxPower } from "@pages/PerimeterSite/PerimeterSiteMaxPower/PerimeterSiteMaxPower";
import { PerimeterSiteOverview } from "@pages/PerimeterSite/PerimeterSiteOverview/PerimeterSiteOverview";
import { TableReadings } from "@pages/PerimeterSite/TableReadings/TableReadings";
import type { UrlParam } from "@router/router.types";
import { useGetCustomer } from "@services/apiEcms/useGetCustomer";
import { useGetCustomerContract } from "@services/apiEcms/useGetCustomerContract";
import { useGetSite } from "@services/apiEcms/useGetSite";
import { errorCode } from "@utils/miscellaneous.utils";
import { useCustomTranslation } from "@utils/translations/useCustomTranslation";

import classes from "./PerimeterSite.module.css";

export const PerimeterSite: FC = () => {
  const { t } = useCustomTranslation("main");
  const {
    customerId = "",
    contractId = "",
    siteId = "",
    energy,
  } = useParams<UrlParam>();

  const { data: customer, isLoading: isCustomerLoading } =
    useGetCustomer(customerId);
  const { data: contract, isLoading: isContractLoading } =
    useGetCustomerContract({
      customerId,
      contractId,
    });
  const { data: site, isLoading: isSiteLoading } = useGetSite({
    customerId,
    contractId,
    siteId,
  });

  const isActiveStatusCode =
    site && !errorCode.includes(site.siteInformation.status.code);

  const { setReferencesFunc } = useReferences();
  const { setSelectedPerimeters } = useSelectedPerimeters();

  useEffect(() => {
    if (site && customer && contract) {
      setReferencesFunc({
        customer: { id: customerId, name: customer.companyName },
        contract: {
          id: contractId,
          name: contract.contractInformation.contractName,
        },
        site: {
          id: siteId,
          name: site.siteInformation.podName,
        },
      });
    }
    return () => {
      setReferencesFunc();
      setSelectedPerimeters([]);
    };
  }, [
    customer,
    contract,
    site,
    customerId,
    contractId,
    siteId,
    setReferencesFunc,
    setSelectedPerimeters,
  ]);

  return (
    <div className={classes.container}>
      <PerimeterSiteOverview
        isOverviewLoading={
          isSiteLoading || isCustomerLoading || isContractLoading
        }
        overviewData={
          site
            ? {
                ...site,
                companyName: customer?.companyName || "",
                contractName: contract?.contractInformation.contractName || "",
              }
            : undefined
        }
      />
      {isActiveStatusCode && (
        <>
          {energy === "ELEC" && <PerimeterSiteMaxPower />}
          <Card className={classes.card} data-testid="PerimeterPageSiteARC">
            <h3 className="font-title-lg">
              {t("perimeter.sites.selfReading.title")}
            </h3>
            <PerimeterSelfReading />
          </Card>
          <TableReadings energy={site.siteInformation.energy} />
        </>
      )}
    </div>
  );
};
